/**
 * Administração → Trilha administrativa (T-15, revisão de segurança de 25/09).
 *
 * Quem criou usuário, mudou perfil, redefiniu senha, emitiu ou revogou chave, mexeu na
 * configuração ou no cadastro da SENATRAN, girou a chave do webhook, baixou a carga
 * histórica — com quando e de onde. Só leitura: a trilha é append-only no banco, e nenhuma
 * tela a edita.
 */
import { useCallback, useEffect, useState } from "react";

import { ErroDaApi, api } from "../api/cliente";
import type { RegistroAdministrativo } from "../api/tipos";
import { Badge, Botao, Estado, Painel } from "../componentes/base";
import { dataEHora } from "../formato";
import { ASSUNTOS, descricaoDaAcao } from "./auditoria";

const POR_PAGINA = 50;

export function TrilhaAdministrativa() {
  const [assunto, definirAssunto] = useState("");
  const [registros, definirRegistros] = useState<
    RegistroAdministrativo[] | null
  >(null);
  const [erro, definirErro] = useState<ErroDaApi | null>(null);
  const [haMais, definirHaMais] = useState(false);

  const carregar = useCallback(
    async (antes?: string) => {
      const p = new URLSearchParams({ limite: String(POR_PAGINA) });
      if (assunto) p.set("assunto", assunto);
      if (antes) p.set("antes", antes);
      try {
        const pagina = await api<RegistroAdministrativo[]>(
          `/admin/auditoria?${p.toString()}`,
        );
        definirRegistros((atuais) =>
          antes && atuais ? [...atuais, ...pagina] : pagina,
        );
        definirHaMais(pagina.length === POR_PAGINA);
        definirErro(null);
      } catch (falha) {
        definirErro(
          falha instanceof ErroDaApi
            ? falha
            : new ErroDaApi("Falha inesperada.", 0),
        );
      }
    },
    [assunto],
  );

  useEffect(() => {
    definirRegistros(null);
    void carregar();
  }, [carregar]);

  return (
    <Painel
      titulo="Ações registradas"
      acoes={
        <span
          className="flex flex-wrap gap-1"
          role="group"
          aria-label="Assunto"
        >
          {ASSUNTOS.map((a) => (
            <Botao
              key={a.chave}
              papel={assunto === a.chave ? "primaria" : "secundaria"}
              onClick={() => definirAssunto(a.chave)}
            >
              {a.rotulo}
            </Botao>
          ))}
        </span>
      }
      rodape={
        <span>
          Não se apaga nem se edita — nem pelo banco. Senha, chave e token nunca
          entram: a trilha diz <i>que</i> a senha foi redefinida, nunca qual.
        </span>
      }
    >
      {erro ? (
        <Estado
          tipo={erro.status === 403 ? "bloqueado" : "erro"}
          titulo={
            erro.status === 403
              ? "Esta área é do administrador da organização."
              : "Não foi possível carregar."
          }
          detalhe={erro.message}
        />
      ) : registros === null ? (
        <Estado tipo="carregando" titulo="Carregando…" />
      ) : registros.length === 0 ? (
        <Estado tipo="vazio" titulo="Nenhuma ação administrativa registrada." />
      ) : (
        <>
          <ol aria-label="Ações administrativas" className="grid gap-1.5">
            {registros.map((r) => (
              <li
                key={r.id}
                className="grid gap-0.5 rounded-[7px] border border-linha bg-painel-2 px-2.5 py-2"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="mono text-[11px] text-tinta-3">
                    {dataEHora(r.em)}
                  </span>
                  <Badge cor="azul">{r.acao.split(".")[0]}</Badge>
                  <span className="text-tinta">{descricaoDaAcao(r)}</span>
                </div>
                <small className="text-[10.5px] text-tinta-3">
                  {r.usuario_nome ??
                    ((r.detalhe as Record<string, unknown>).origem ===
                    "linha_de_comando"
                      ? "linha de comando, na VM"
                      : "sistema")}
                  {r.usuario_email && ` · ${r.usuario_email}`}
                  {r.ip && ` · de ${r.ip}`}
                </small>
              </li>
            ))}
          </ol>
          {haMais && (
            <Botao
              onClick={() => void carregar(registros[registros.length - 1]!.em)}
            >
              Carregar mais
            </Botao>
          )}
        </>
      )}
    </Painel>
  );
}
